// RecommendationConfirmModal.js - Confirmation modal showing parameter comparison
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const RecommendationConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  genericParams,
  catalogComponent,
  frequency
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ top: null, left: null });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) {
      setPosition({ top: null, left: null });
    }
  }, [isOpen]);

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    const rect = e.currentTarget.closest('[data-modal-container]').getBoundingClientRect();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      left: e.clientX - dragOffset.x,
      top: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (!isOpen || !catalogComponent) return null;

  const catalogParams = catalogComponent.interpolatedParams || {};

  // Define parameters to compare
  const paramConfig = [
    { key: 'gain', label: 'Gain', unit: 'dB', decimals: 1, higherBetter: true },
    { key: 'noiseFigure', label: 'NF', unit: 'dB', decimals: 2, higherBetter: false },
    { key: 'p1db', label: 'OP1dB', unit: 'dBm', decimals: 1, higherBetter: true },
    { key: 'oip3', label: 'OIP3', unit: 'dBm', decimals: 1, higherBetter: true }
  ];

  const formatValue = (value, decimals) => {
    if (value === null || value === undefined) return 'N/A';
    return Number(value).toFixed(decimals);
  };

  const getMeetsOrExceeds = (generic, catalog, higherBetter) => {
    if (generic === null || generic === undefined || catalog === null || catalog === undefined) {
      return false;
    }
    // For NF, lower is better; for Gain/P1dB/OIP3, higher is better
    if (higherBetter) {
      return catalog >= generic;
    }
    return catalog <= generic;
  };

  return ReactDOM.createPortal(
    <>
      <ModalBackdrop onClick={onClose} />
      <ModalContainer
        data-modal-container
        style={position.top !== null ? { top: position.top, left: position.left, transform: 'none' } : {}}
      >
        <ModalHeader onMouseDown={handleMouseDown} $isDragging={isDragging}>
          <ModalTitle>Replace with {catalogComponent.partNumber}?</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <ComparisonTable>
            <TableHeader>
              <HeaderCell></HeaderCell>
              <HeaderCell>Generic</HeaderCell>
              <HeaderCell>{catalogComponent.partNumber}</HeaderCell>
            </TableHeader>

            {paramConfig.map(({ key, label, unit, decimals, higherBetter }) => {
              const genericVal = genericParams[key]?.value;
              const catalogVal = catalogParams[key];
              const meetsOrExceeds = getMeetsOrExceeds(genericVal, catalogVal, higherBetter);

              return (
                <TableRow key={key}>
                  <LabelCell>{label}:</LabelCell>
                  <ValueCell>{formatValue(genericVal, decimals)} {unit}</ValueCell>
                  <ValueCell $highlight={meetsOrExceeds}>
                    {formatValue(catalogVal, decimals)} {unit}
                    {meetsOrExceeds && <CheckMark>&#10003;</CheckMark>}
                  </ValueCell>
                </TableRow>
              );
            })}
          </ComparisonTable>

          <ScoreSection>
            <ScoreLabel>Match Score:</ScoreLabel>
            <ScoreValue $score={catalogComponent.matchScore}>
              {catalogComponent.matchScore}%
            </ScoreValue>
          </ScoreSection>

          <FrequencyNote>
            Parameters shown at {frequency} MHz
          </FrequencyNote>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ConfirmButton onClick={onConfirm}>Replace</ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </>,
    document.body
  );
};

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1001;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 380px;
  background: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  z-index: 1002;
  overflow: hidden;
  animation: modalIn 0.2s ease;

  @keyframes modalIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 16px 20px;
  background: #1e1e1e;
  border-bottom: 1px solid #404040;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #e0e0e0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: #808080;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #e0e0e0;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ComparisonTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 16px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #404040;
  margin-bottom: 4px;
`;

const HeaderCell = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #808080;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;

  &:first-child {
    text-align: left;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 1fr;
  gap: 8px;
  padding: 6px 0;
`;

const LabelCell = styled.div`
  font-size: 12px;
  color: #808080;
  text-align: left;
`;

const ValueCell = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$highlight ? '#22c55e' : '#e0e0e0'};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const CheckMark = styled.span`
  color: #22c55e;
  font-weight: bold;
  font-size: 14px;
`;

const ScoreSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #404040;
`;

const ScoreLabel = styled.span`
  font-size: 13px;
  color: #808080;
`;

const ScoreValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${props => {
    if (props.$score >= 85) return '#22c55e';
    if (props.$score >= 70) return '#3b82f6';
    return '#eab308';
  }};
`;

const FrequencyNote = styled.div`
  text-align: center;
  font-size: 10px;
  color: #606060;
  margin-top: 8px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background: #1e1e1e;
  border-top: 1px solid #404040;
`;

const Button = styled.button`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid #404040;
  color: #b0b0b0;

  &:hover {
    background: #363636;
    border-color: #505050;
  }
`;

const ConfirmButton = styled(Button)`
  background: #DC143C;
  border: 1px solid #DC143C;
  color: white;

  &:hover {
    background: #b91c3c;
    border-color: #b91c3c;
  }
`;

export default RecommendationConfirmModal;
